import { useLocation, useNavigate } from 'react-router-dom';
import { HouseSimple, CaretLeft } from '@phosphor-icons/react';

/** Static path -> Persian label, mirrors the routes registered in App.jsx. */
const ROUTE_LABELS = {
  '/dashboard': 'داشبورد',
  '/tickets/new': 'تیکت جدید',
  '/help': 'راهنمای هوشمند',
  '/tickets/responded': 'تیکت‌های پاسخ‌داده‌شده',
  '/tickets': 'تیکت‌ها',
  '/tickets/referred': 'تیکت‌های ارجاع‌شده',
  '/dispatcher': 'صف دیسپچر',
  '/dispatcher/assigned': 'ارجاع‌شده به من',
  '/dispatcher/departments': 'داشبورد دپارتمان‌ها',
  '/dispatcher/daily-report-routing': 'مسیریابی گزارش روزانه',
  '/users': 'کاربران',
  '/employees': 'کارمندان',
  '/users/add/company': 'افزودن کاربر',
  '/users/add/employee': 'افزودن کارمند',
  '/profile': 'پروفایل',
  '/profile/edit': 'ویرایش پروفایل',
  '/companies': 'شرکت‌ها',
  '/companies/add': 'افزودن شرکت',
  '/departments': 'دپارتمان‌ها',
  '/departments/add': 'افزودن دپارتمان',
  '/reports/daily': 'گزارش روزانه',
  '/reports/invoices': 'فاکتورها',
  '/reports/manager': 'گزارش روزانه (مدیر)',
  '/reports/distance': 'گزارش مسافت',
  '/reports/presence': 'گزارش حضور',
  '/activation-history': 'تاریخچه فعال‌سازی',
  '/feedback': 'بازخورد',
  '/feedback/mine': 'بازخورد من',
  '/feedback/team': 'نظرسنجی',
  '/ai': 'هوش مصنوعی',
  '/ai/review': 'بررسی پاسخ هوش مصنوعی',
  '/ai/insights': 'تحلیل هوش مصنوعی',
  '/ai/manager': 'هوش مصنوعی مدیر',
  '/ai/statistics': 'آمار هوش مصنوعی',
  '/ai/dataset': 'مجموعه داده آموزشی',
  '/map': 'نقشه',
  '/map/add-place': 'افزودن مکان',
  '/map/places': 'مکان‌ها',
  '/map/test': 'تست نقشه',
  '/settings': 'تنظیمات',
  '/settings/menu': 'ترتیب منو',
  '/settings/signature': 'امضا',
  '/settings/daily-report-items': 'موارد گزارش روزانه',
  '/settings/auto-referral': 'ارجاع خودکار',
  '/settings/cross-referral': 'تنظیمات ارجاع متقابل',
  '/settings/voip-trunks': 'خطوط VoIP',
  '/settings/gps-known-places': 'مکان‌های شناخته‌شده GPS',
  '/reports/gps-daily-report': 'گزارش روزانه GPS',
  '/reports/employee-response-ratings': 'امتیاز پاسخ کارمندان',
  '/settings/manager-company-permissions': 'دسترسی شرکت‌ها',
  '/settings/company-delegation': 'تفویض شرکت',
  '/settings/username-prefix': 'جداکننده نام کاربری',
  '/sms-campaigns': 'کمپین پیامکی',
  '/settings/access-control': 'کنترل دسترسی',
  '/missions/define': 'تعریف ماموریت',
  '/missions/my': 'ماموریت‌های من',
};

/** Dynamic segments (`:id`) matched by regex, in most-specific-first order. */
const DYNAMIC_ROUTES = [
  { test: /^\/tickets\/(\d+)\/process$/, label: (m) => `پردازش تیکت #${m[1]}` },
  { test: /^\/tickets\/(\d+)\/assign$/, label: (m) => `ارجاع تیکت #${m[1]}` },
  { test: /^\/tickets\/(\d+)\/reassign$/, label: (m) => `ارجاع مجدد تیکت #${m[1]}` },
  { test: /^\/tickets\/(\d+)$/, label: (m) => `تیکت #${m[1]}` },
  { test: /^\/companies\/(\d+)$/, label: () => 'جزئیات شرکت' },
  { test: /^\/missions\/(\d+)\/respond$/, label: () => 'پاسخ به ماموریت' },
];

function labelFor(pathname) {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  for (const route of DYNAMIC_ROUTES) {
    const m = pathname.match(route.test);
    if (m) return route.label(m);
  }
  return null;
}

/** Builds the crumb trail for a pathname, always rooted at the dashboard. */
function buildTrail(pathname) {
  const trail = [{ label: 'داشبورد', path: '/dashboard' }];
  if (pathname === '/dashboard' || pathname === '/home') return trail;

  const segments = pathname.split('/').filter(Boolean);
  let acc = '';
  for (let i = 0; i < segments.length; i++) {
    acc += `/${segments[i]}`;
    const label = labelFor(acc);
    if (label) {
      // Avoid duplicating the dashboard root crumb if a route resolves to it.
      if (acc === '/dashboard') continue;
      trail.push({ label, path: acc });
    }
  }
  // Nothing matched below the root (unknown/legacy route) - still show something.
  if (trail.length === 1) {
    trail.push({ label: segments[segments.length - 1] || pathname, path: pathname });
  }
  return trail;
}

// `path` lets a caller override the router's own location - needed inside
// the employee multi-tab workspace, where switching/opening tabs never
// changes the actual URL (it always stays /dashboard), so useLocation()
// alone can't tell which tab's page is showing. See MultiTabWorkspace.jsx.
export default function Breadcrumb({ path, onNavigate }) {
  const { pathname: routerPathname } = useLocation();
  const routerNavigate = useNavigate();
  const pathname = path || routerPathname;
  const navigate = onNavigate || routerNavigate;

  if (pathname === '/dashboard' || pathname === '/home') return null;

  const trail = buildTrail(pathname);

  return (
    <div className="pj-breadcrumb-row">
      <nav className="pj-breadcrumb" aria-label="مسیر صفحه">
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          return (
            <span key={crumb.path + i} className="pj-breadcrumb-item">
              {i === 0 && <HouseSimple size={14} weight="fill" className="pj-breadcrumb-home-icon" />}
              {isLast ? (
                <span className="pj-breadcrumb-current">{crumb.label}</span>
              ) : (
                <button type="button" className="pj-breadcrumb-link" onClick={() => navigate(crumb.path)}>
                  {crumb.label}
                </button>
              )}
              {!isLast && <CaretLeft size={11} className="pj-breadcrumb-sep" />}
            </span>
          );
        })}
      </nav>
    </div>
  );
}
